import { useEffect, useMemo, useState } from "react";
import { Button, Group, Modal, Paper, Select, Space, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { getDoc, doc, onSnapshot, writeBatch } from "firebase/firestore";
import { useFirestore, useFunctions } from "../helpers/firebaseContext";
import { CustomAutocomplete, Customer, FamilyRelative, familyRelations, LinkFamilyMemberRequest, BaseResult } from "../types";
import { CustomerSelectComponent } from "./customer_select_component";
import { httpsCallable } from "firebase/functions";
import { modals } from "@mantine/modals";

interface AddFamilyMemberModalProps {
  opened: boolean;
  setOpened: (value: boolean) => void;
  customerData: Customer;
}

const relationOptions = Object.entries(familyRelations).map(([key, label]) => ({
  value: key,
  label,
}));

const relationKeyFromValue = (value: string | undefined) => {
  if (!value) return "other";
  if (value in familyRelations) {
    return value as keyof typeof familyRelations;
  }
  const found = Object.entries(familyRelations).find(([, label]) => label === value);
  return (found?.[0] as keyof typeof familyRelations) || "other";
};
const AddFamilyMemberModal = ({ opened, setOpened, customerData }: AddFamilyMemberModalProps) => {
  const db = useFirestore();
  const functions = useFunctions();
  functions.region = "europe-west1";

  const [selectedCustomer, setSelectedCustomer] = useState<(Customer & CustomAutocomplete) | null>(null);
  const [relation, setRelation] = useState<keyof typeof familyRelations | null>(null);
  const [pending, setPending] = useState(false);
  const [relatives, setRelatives] = useState<FamilyRelative[]>([]);
  const [loadingRelatives, setLoadingRelatives] = useState(false);
  const [relationEdits, setRelationEdits] = useState<Record<string, keyof typeof familyRelations>>({});
  const [rowPending, setRowPending] = useState<Record<string, boolean>>({});
  const linkFamilyMember = httpsCallable<LinkFamilyMemberRequest, BaseResult>(functions, "linkFamilyMember");

  useEffect(() => {
    if (!opened) {
      setSelectedCustomer(null);
      setRelation(null);
      setPending(false);
      setRelatives([]);
      setLoadingRelatives(false);
      setRelationEdits({});
      setRowPending({});
    }
  }, [opened]);

  useEffect(() => {
    if (!opened || !customerData?.id) {
      return;
    }

    setLoadingRelatives(true);
    const customerRef = doc(db, "customers", customerData.id as string);
    const unsubscribe = onSnapshot(
      customerRef,
      (snapshot) => {
        const data = snapshot.data() as Customer | undefined;
        const relativesMap = (data?.relatives || {}) as Record<string, FamilyRelative>;
        const items = Object.entries(relativesMap).map(([id, relative]) => ({
          ...relative,
        })) as FamilyRelative[];
        setRelatives(items);
        setRelationEdits((prev) => {
          const next: Record<string, keyof typeof familyRelations> = {};
          items.forEach((relative: FamilyRelative) => {
            const relativeId = relative.relative_id;
            if (relativeId) {
              next[relativeId] = prev[relativeId] || relationKeyFromValue(relative.relation);
            }
          });
          return next;
        });
        setLoadingRelatives(false);
      },
      (error) => {
        console.error("Failed to load relatives", error);
        setRelatives([]);
        setLoadingRelatives(false);
      },
    );

    return () => unsubscribe();
  }, [opened, customerData?.id, db]);

  const selectedCustomerLabel = useMemo(() => {
    if (!selectedCustomer) return null;
    const idDisplay = selectedCustomer.civilID || selectedCustomer.commercial_register || selectedCustomer.id || "";
    return `${idDisplay} - ${selectedCustomer.name_ar} - ${selectedCustomer.name_en}`;
  }, [selectedCustomer]);


  const handleRelativeCreateUpdate = async (selectedCustomer: Customer, relationValue: familyRelations, handleComplete: (val: any) => void) => {
    try {
      await linkFamilyMember({ customerId: customerData.id!, relation: relationValue, relative_id: selectedCustomer.id! });
      setRelation(null);
      notifications.show({
        color: "green",
        message: "Family member added",
      });
    } catch (error: any) {
      console.error("Failed to add family member", error);
      notifications.show({
        color: "red",
        message: error?.message || "Failed to add family member",
      });
    } finally {
      handleComplete(false);
    }
  }
  const handleSubmit = async () => {
    if (!customerData?.id) {
      notifications.show({
        color: "red",
        message: "Customer ID is missing",
      });
      return;
    }
    if (!selectedCustomer?.id) {
      notifications.show({
        color: "red",
        message: "Please select a family member",
      });
      return;
    }
    if (selectedCustomer.id === customerData.id) {
      notifications.show({
        color: "red",
        message: "You cannot add the same customer as a relative",
      });
      return;
    }
    if (!relation) {
      notifications.show({
        color: "red",
        message: "Please select a relationship",
      });
      return;
    }

    const relationValue = familyRelations[relation];
    setPending(true);
    handleRelativeCreateUpdate(selectedCustomer, relationValue, setPending)

  };

  const handleUpdateRelation = async (relative: FamilyRelative) => {
    const relativeId = relative.relative_id;
    if (!relativeId || !customerData?.id) {
      notifications.show({
        color: "red",
        message: "Relative ID is missing",
      });
      return;
    }
    const newRelationKey = relationEdits[relativeId] || relationKeyFromValue(relative.relation);
    const newRelationValue = familyRelations[newRelationKey];

    setRowPending((prev) => ({ ...prev, [relativeId]: true }));
    handleRelativeCreateUpdate({
      name_en: relative.relative_name_en,
      id: relative.relative_id,
      name_ar: relative.relative_name_ar,
      civilID: relative.relative_civilID!,
      crmID: relative.relative_crmID!,
    } as Customer, newRelationValue, () => setRowPending((prev) => ({ ...prev, [relativeId]: false })))
  };

  const handleDeleteRelation = async (relative: FamilyRelative) => {
    const relativeId = relative.relative_id;
    if (!relativeId || !customerData?.id) {
      notifications.show({ color: "red", message: "Relative id is missing" });
      return;
    }

    setRowPending((prev) => ({ ...prev, [relativeId]: true }));
    try {
      const currentRef = doc(db, "customers", customerData.id);
      const selectedRef = doc(db, "customers", relativeId);

      const [currentSnap, selectedSnap] = await Promise.all([getDoc(currentRef), getDoc(selectedRef)]);

      // handle both old map shape and new array shape
      const toArr = (v: any): FamilyRelative[] =>
        Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

      const currentArr = toArr(currentSnap.data()?.relatives).filter((r) => r.relative_id !== relativeId);
      const selectedArr = toArr(selectedSnap.data()?.relatives).filter((r) => r.relative_id !== customerData.id);

      const batch = writeBatch(db);
      batch.update(currentRef, { relatives: currentArr });
      batch.update(selectedRef, { relatives: selectedArr });
      await batch.commit();

      notifications.show({ color: "green", message: "Relationship removed" });
    } catch (error: any) {
      console.error("Failed to remove relationship", error);
      notifications.show({ color: "red", message: error?.message || "Failed to remove relationship" });
    } finally {
      setRowPending((prev) => ({ ...prev, [relativeId]: false }));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      title="Family Members"
      size="xl"
    >
      <CustomerSelectComponent
        submitType="object"
        handleSubmit={(customer) => {
          if (!customer) {
            setSelectedCustomer(null);
            return;
          }
          setSelectedCustomer(customer as Customer & CustomAutocomplete);
        }}
        textProps={{
          label: "Customer",
          placeholder: "Search for a customer",
        }}
      />

      <Space h="md" />

      <Select
        data={relationOptions}
        label="Relationship"
        placeholder="Select relationship"
        value={relation}
        onChange={(value) => {
          if (!value) return;
          setRelation(value as keyof typeof familyRelations);
        }}
        allowDeselect={false}
      />

      {selectedCustomerLabel && (
        <>
          <Space h="md" />
          <Text size="sm">Selected: {selectedCustomerLabel}</Text>
        </>
      )}
      <Space h="md" />

      <Text fw={600}>Existing relatives</Text>
      {loadingRelatives && <Text size="sm">Loading relatives...</Text>}
      {!loadingRelatives && relatives.length === 0 && (
        <Text size="sm" c="dimmed">No relatives added yet.</Text>
      )}

      {!loadingRelatives && relatives.length > 0 && relatives.map((relative) => {
        const relativeId = relative.relative_id;
        const nameAr = relative.relative_name_ar || "";
        const nameEn = relative.relative_name_en || "";
        const idDisplay = relative.relative_civilID || relative.relative_crmID || relativeId || "";
        const currentRelation = relationEdits[relativeId] || relationKeyFromValue(relative.relation);
        const isPending = !!rowPending[relativeId];
        return (
          <Paper key={relativeId} shadow="xs" p="sm" withBorder>
            <Group key={relativeId} align="flex-end" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Text size="sm">{idDisplay} - {nameAr} - {nameEn}</Text>
              </div>
              <Select
                data={relationOptions}
                value={currentRelation}
                onChange={(value) => {
                  if (!relativeId || !value) return;
                  setRelationEdits((prev) => ({ ...prev, [relativeId]: value as keyof typeof familyRelations }));
                }}
                disabled={isPending}
              />
              <Button
                size="xs"
                loading={isPending}
                disabled={currentRelation === relationKeyFromValue(relative.relation)}
                onClick={() => handleUpdateRelation(relative)}
              >
                Save
              </Button>
              <Button
                size="xs"
                color="red"
                loading={isPending}
                onClick={() =>
                  modals.openConfirmModal({
                    title: "Remove relationship",
                    children: (
                      <Text size="sm">
                        Remove <b>{relative.relative_name_en || relative.relative_id}</b> as a relative?
                        This also removes the reciprocal link on their record.
                      </Text>
                    ),
                    labels: { confirm: "Remove", cancel: "Cancel" },
                    confirmProps: { color: "red" },
                    onConfirm: () => handleDeleteRelation(relative),
                  })
                }
              >
                Delete
              </Button>
            </Group>
          </Paper>
        );
      })}



      <Space h="md" />

      <Group justify="flex-end">
        <Button variant="default" onClick={() => setOpened(false)}>
          Cancel
        </Button>
        <Button loading={pending} onClick={handleSubmit}>
          save
        </Button>
      </Group>
    </Modal>
  );
};

export default AddFamilyMemberModal;
